import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { CategoryWithArticles, FAQ, HelpArticle, HelpCategory, HelpSearchResults } from "../types/HelpArticle";
import type { CreateArticleParams, CreateCategoryParams, CreateFAQParams, IHelpAPI, UpdateArticleParams, UpdateCategoryParams, UpdateFAQParams } from "./helpApi";

class RemoteHelpAPI implements IHelpAPI {
  async fetchCategories(): Promise<HelpCategory[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/help/categories`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to fetch categories", error);
      throw error;
    }
  }

  async fetchCategoryBySlug(slug: string): Promise<CategoryWithArticles> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/help/category/${slug}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Category not found");
        }
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to fetch category", error);
      throw error;
    }
  }

  async fetchArticleBySlug(slug: string): Promise<HelpArticle> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/help/article/${slug}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Article not found");
        }
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to fetch article", error);
      throw error;
    }
  }

  async searchHelp(query: string): Promise<HelpSearchResults> {
    try {
      const encodedQuery = encodeURIComponent(query);
      const response = await fetch(`${API_BASE_URL}/api/help/search?q=${encodedQuery}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to search help content", error);
      throw error;
    }
  }

  async fetchFAQs(): Promise<FAQ[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/help/faqs`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to fetch FAQs", error);
      throw error;
    }
  }

  // ==================== ADMIN METHODS ====================

  // Admin - Categories
  async createCategory(params: CreateCategoryParams): Promise<HelpCategory> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to create category", error);
      throw error;
    }
  }

  async updateCategory(id: string, params: UpdateCategoryParams): Promise<HelpCategory> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/categories/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to update category", error);
      throw error;
    }
  }

  // Admin - Articles
  async getAllArticles(): Promise<HelpArticle[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/articles`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to fetch all articles", error);
      throw error;
    }
  }

  async createArticle(params: CreateArticleParams): Promise<HelpArticle> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/articles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to create article", error);
      throw error;
    }
  }

  async updateArticle(id: string, params: UpdateArticleParams): Promise<HelpArticle> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/articles/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to update article", error);
      throw error;
    }
  }

  async deleteArticle(id: string): Promise<void> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/articles/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
    } catch (error) {
      log("remoteHelpApi", "Failed to delete article", error);
      throw error;
    }
  }

  async uploadArticleImage(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append("image", file);

      // Note: do NOT set Content-Type — the browser sets the multipart boundary.
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/articles/image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.url;
    } catch (error) {
      log("remoteHelpApi", "Failed to upload article image", error);
      throw error;
    }
  }

  // Admin - FAQs
  async createFAQ(params: CreateFAQParams): Promise<FAQ> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/faqs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to create FAQ", error);
      throw error;
    }
  }

  async updateFAQ(id: string, params: UpdateFAQParams): Promise<FAQ> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/help/faqs/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteHelpApi", "Failed to update FAQ", error);
      throw error;
    }
  }
}

export default RemoteHelpAPI;
